/*
 * Copyright (c) 2025. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {
  type SxProps,
  TextField,
  type TextFieldProps,
  type Theme,
  Typography,
} from '@mui/material';
import { prettifySnakeCase } from '@poweralpha/common';
import { type ChangeEvent, type ReactNode, useCallback, useMemo } from 'react';
import { useFieldContext } from './formContext';

interface Props
  extends Omit<
    TextFieldProps,
    'error' | 'helperText' | 'label' | 'maxRows' | 'minRows' | 'multiline' | 'onChange' | 'value'
  > {
  label?: ReactNode;
  height?: number;
  minHeight?: number;
  minRows?: number;
  maxRows?: number;
  hideLabel?: boolean;
  hideErrorText?: boolean;
  disableOutline?: boolean;
  displayOnly?: boolean;
  disableDrag?: boolean;
  maxLength?: number;
  'data-testid'?: string;
}

const normalizeSx = (sx: TextFieldProps['sx']) => {
  if (!sx) return [];
  if (Array.isArray(sx)) return sx;
  return [sx];
};

const getTextFieldSx = ({
  disableDrag,
  disableOutline,
  height,
  minHeight,
  sx,
}: Pick<Props, 'disableDrag' | 'disableOutline' | 'height' | 'minHeight' | 'sx'>) => {
  const inputRootSx = {
    alignItems: 'flex-start',
    ...(minHeight ? { minHeight } : {}),
    ...(height ? { height } : {}),
  };
  const outlineSx = disableOutline
    ? {
        '& .MuiOutlinedInput-notchedOutline': {
          border: 0,
        },
      }
    : {};

  return [
    {
      '& .MuiInputBase-root': inputRootSx,
      '& textarea': {
        resize: disableDrag ? 'none' : 'vertical',
      },
      ...outlineSx,
    },
    ...normalizeSx(sx),
  ] as SxProps<Theme>;
};

function FormTextArea({
  label,
  minHeight,
  height,
  hideLabel,
  hideErrorText,
  disableOutline = false,
  displayOnly,
  disableDrag = false,
  minRows = 3,
  maxRows = 6,
  maxLength,
  placeholder,
  sx,
  ...props
}: Readonly<Props>) {
  const field = useFieldContext<string>();
  const fieldKey = field.name.split('.').at(-1) ?? field.name;

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => field.handleChange(e.target.value),
    [field],
  );

  const error = useMemo(() => field.state.meta.errors[0]?.message, [field.state.meta.errors]);
  const resolvedLabel = label ?? prettifySnakeCase(field.name.split('.').at(-1));
  const resolvedPlaceholder =
    placeholder ?? (hideLabel && typeof resolvedLabel === 'string' ? resolvedLabel : undefined);
  const textFieldSx = getTextFieldSx({ disableDrag, disableOutline, height, minHeight, sx });

  return (
    <>
      {displayOnly ? (
        <Typography
          sx={{
            fontSize: 14,
            whiteSpace: 'pre-wrap',
          }}
        >
          {field.state.value}
        </Typography>
      ) : (
        <TextField
          {...props}
          fullWidth
          multiline
          size={props.size ?? 'small'}
          label={hideLabel ? undefined : resolvedLabel}
          placeholder={resolvedPlaceholder}
          data-testid={props['data-testid'] ?? fieldKey}
          value={field.state.value ?? ''}
          name={field.name}
          onChange={handleChange}
          minRows={minRows}
          maxRows={maxRows}
          error={Boolean(error)}
          helperText={hideErrorText ? '' : error}
          slotProps={{
            htmlInput: {
              maxLength,
            },
          }}
          sx={textFieldSx}
        />
      )}
    </>
  );
}

export default FormTextArea;
